import { useState } from "react"
import ErrorMessage from "@/v2-ui/ErrorMessage"
import Input from "@/v2-ui/input"
import cn from "@/v2-ui/utils/utils.cn"
import {
  pluralize,
  type FeatureGroupPickedLike,
} from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.compose.utils"
import { useFeatureBrowseQuery } from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.browse.query"
import { useFeatureBrowseGroupsQuery } from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.browseGroups.query"
import LicenseFeatureGroupRegistryFamiliesPane from "@/v2-backoffice-client/license/featureGroup/compose/registry/LicenseFeatureGroupRegistryFamiliesPane"
import LicenseFeatureGroupRegistryFeaturesPane from "@/v2-backoffice-client/license/featureGroup/compose/registry/LicenseFeatureGroupRegistryFeaturesPane"
import LicenseFeatureGroupRegistryTypesPane from "@/v2-backoffice-client/license/featureGroup/compose/registry/LicenseFeatureGroupRegistryTypesPane"
import { typesOf } from "@/v2-backoffice-client/license/featureGroup/compose/registry/license.featureGroup.registry.utils"

export type LicenseFeatureGroupRegistryBrowserProps = {
  /** The feature types the app enforces; a feature of another type is flagged as doing nothing. */
  entitledTypeCodes: ReadonlySet<number>
  featureGroupName: string
  isPicked(code: string): boolean
  wasPicked(code: string): boolean
  onSetPicked(
    features: readonly FeatureGroupPickedLike[],
    picked: boolean,
  ): void
  className?: string
}

const MIN_SEARCH_LENGTH = 2
/** backoffice.Get_Common_FeaturesBrowse caps its rows here. */
const ROW_LIMIT = 200

// The registry, browsable: every grantable feature type, the code families
// inside the chosen type, and the features inside the chosen family, each a
// pane with counts — so an operator finds a nav layer or a core feature by
// reading, not by knowing its code. Column-browser rules: a pane is empty
// until the pane before it has a selection, and the families pane opens on
// an "All" row so the features pane always shows exactly what the selection
// says. The filter prunes all three panes to the matches instead of
// replacing them with a result list. This component holds the selection
// and the two reads; the panes draw.
function LicenseFeatureGroupRegistryBrowser(
  props: LicenseFeatureGroupRegistryBrowserProps,
) {
  const {
    entitledTypeCodes,
    featureGroupName,
    isPicked,
    wasPicked,
    onSetPicked,
    className,
  } = props
  const [search, setSearch] = useState("")
  const [typeCode, setTypeCode] = useState<number | null>(null)
  const [family, setFamily] = useState<string | null>(null)

  const trimmed = search.trim()
  const searchArgument = trimmed.length >= MIN_SEARCH_LENGTH ? trimmed : null
  const isSearching = searchArgument !== null

  const groupsQuery = useFeatureBrowseGroupsQuery({
    variables: { search: searchArgument },
  })
  const groups =
    (groupsQuery.data ?? groupsQuery.previousData)?.getFeatureBrowseGroups ?? []
  const types = typesOf(groups)
  const selectedType = types.find((type) => type.code === typeCode) ?? null
  const families = groups.filter((group) => group.FeatureTypeCode === typeCode)
  // A selection the filter pruned away no longer narrows the rows.
  const effectiveType = selectedType ? typeCode : null
  const effectiveFamily =
    selectedType && families.some((group) => group.Family === family)
      ? family
      : null
  const hasScope = effectiveType !== null || isSearching

  const rowsQuery = useFeatureBrowseQuery({
    variables: {
      featureTypeCode: effectiveType,
      family: effectiveFamily,
      search: searchArgument,
    },
    skip: !hasScope,
  })
  const rows = hasScope
    ? ((rowsQuery.data ?? rowsQuery.previousData)?.browseFeatures ?? [])
    : []
  let total = 0
  for (const type of types) total += type.count

  return (
    <section
      className={cn("flex min-h-0 flex-col gap-2 rounded-md border", className)}
    >
      <div className="flex shrink-0 items-center gap-3 border-b bg-muted/40 px-2.5 py-1.5">
        <span className="min-w-0 flex-1 truncate text-xs font-medium">
          The registry
        </span>
        <Input
          type="search"
          size="tiny"
          className="w-56"
          placeholder="Filter by name or code…"
          aria-label="Filter the registry"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value)
          }}
        />
      </div>
      <ErrorMessage error={groupsQuery.error ?? rowsQuery.error} />
      <div className="grid min-h-0 flex-1 grid-cols-[minmax(10rem,1fr)_minmax(12rem,1.2fr)_minmax(0,3fr)] grid-rows-[minmax(0,1fr)] divide-x">
        <LicenseFeatureGroupRegistryTypesPane
          types={types}
          isLoading={groupsQuery.loading}
          selectedTypeCode={effectiveType}
          onSelect={(next) => {
            setTypeCode(next)
            setFamily(null)
          }}
        />
        <LicenseFeatureGroupRegistryFamiliesPane
          type={selectedType}
          families={families}
          selectedFamily={effectiveFamily}
          onSelect={setFamily}
        />
        <LicenseFeatureGroupRegistryFeaturesPane
          rows={rows}
          hasScope={hasScope}
          isLoading={rowsQuery.loading}
          showsType={effectiveType === null}
          rowLimit={ROW_LIMIT}
          entitledTypeCodes={entitledTypeCodes}
          featureGroupName={featureGroupName}
          isPicked={isPicked}
          wasPicked={wasPicked}
          onSetPicked={onSetPicked}
        />
      </div>
      <p className="shrink-0 border-t px-2.5 py-1 text-[11px] text-muted-foreground">
        {types.length === 0
          ? "Every feature a package can carry, folded by type and code family."
          : `${pluralize(total, "grantable feature")} in ${pluralize(types.length, "type")}${isSearching ? ` match “${trimmed}”` : ""}.`}
      </p>
    </section>
  )
}

export default LicenseFeatureGroupRegistryBrowser
